"use client";

import { useEffect } from "react";
import { createPortal } from "react-dom";

interface CareerAppliedModalProps {
  open: boolean;
  onClose: () => void;
  onViewMoreJobs?: () => void;
}

// Shared "Registration Completed" confirmation — originally only on the
// /careers list page (triggered by a ?applied=success redirect from the old
// career.vilvabusiness.com registration flow), now also used inline on the
// job detail page once the new in-page application form finishes, with no
// redirect at all. Rendered via a portal so it always escapes whatever
// stacking context its host page wraps it in.
export default function CareerAppliedModal({ open, onClose, onViewMoreJobs }: CareerAppliedModalProps) {
  useEffect(() => {
    if (open) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="applied-success-title"
        className="w-full max-w-md rounded-2xl bg-white dark:bg-[#0B1120] p-8 text-center shadow-xl border border-transparent dark:border-[#1F2A44]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#E7F6EC] dark:bg-[#123023] text-[32px]">
          🎉
        </div>
        <h3 id="applied-success-title" className="font-NeueMontreal text-[22px] font-bold text-[#090D28] dark:text-[#E5E7EB]">
          Registration Completed!
        </h3>
        <p className="mt-3 font-Schibsted_Grotesk text-[15px] text-[#2C2845] dark:text-[#B8C0D9]">
          Thanks for applying — check your email shortly, you&apos;ll receive your task there.
        </p>
        <p className="mt-2 font-Schibsted_Grotesk text-[13px] text-[#6C7085] dark:text-[#7C87A5]">
          Our team reviews every application and will reach out with next steps once it&apos;s your turn.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          {onViewMoreJobs && (
            <button
              type="button"
              onClick={onViewMoreJobs}
              className="rounded-[8px] bg-[#195AFF] px-4 py-2.5 font-Schibsted_Grotesk text-sm font-semibold text-white transition hover:bg-[#1448CC]"
            >
              View more jobs
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-[8px] px-4 py-2 font-Schibsted_Grotesk text-sm font-semibold text-[#6C7085] dark:text-[#B8C0D9] transition hover:text-[#195AFF]"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
